import { gql } from '@apollo/client';

import { fragments as patientFragments } from './patients';
import { fragments as staffFragments } from './staff';

const baseFields = `
  id
  visitNumber
  procedureHstId
  status
  startTime
  startTimeText
  endTimeText
  duration
  orId
  or
  isCanceled
  physicianId
  physicianHstId
  physicianName
  physicianNameHST
  physicianColor
  patientId
  patientHstId
  patientName
  patientNameUnformatted
  patientInitials
  patientDateOfBirth
  patientDateOfBirthISO
  patientAge
  patientSex
  patientHomePhone
  patientCellPhone
  caretakerCellPhone
  familyCellPhone
  patientEmailAddress
  patientAddress
  procedureTypeId
  procedureType
  procedureMod
  diagnosis
  equipment
  cptCodes
  bilaterally
  visitType
  room
  insuranceName
  policyName
  anesthesiologistId
  anesthesiologistHstId
  anesthesiologistName
  anesthesiaType
  allergies
  notes
  anesthesiologistNotes
  orNotes
  preOpNote
  visit
  preferredLanguage
  procedurePriority
  color
  date
  caretakerThread {
    id
    hasUnreadMessages
  }
  patientThread {
    id
    hasUnreadMessages
  }
  vendorRepresentative {
    id
    vendor {
      name
    }
    implant
    price
    representative {
      name
      email
      phoneNumber
    }
  }
`;

export const fragments = {
  all: gql`
    fragment ProcedureData on Procedure {
      ${baseFields}
      physician {
        id
        name
        color
        notificationNumbers
        email
        currentRoomId
        room {
          id
          name
        }
      }
      patient {
        id
        name
        nameUnformatted
        type
        initials
        status
        room {
          id
          type
          name
        }
        bed
        ...PatientEvents
        ...PatientLog
      }
      anesthesiologist {
        id
        name
        phoneNumber
        email
      }
      assignedVendors {
        id
        procedureId
        vendor {
          id
          name
        }
        shiftState
      }
    }
    ${patientFragments.patientEvents}
    ${patientFragments.patientLog}
  `,
  schedule: gql`
    fragment ProcedureScheduleFragment on Procedure {
      id
      visitNumber
      date
      startTime
      startTimeText
      endTimeText
      duration
      orId
      or
      room
      status
      isCanceled
      color
      procedurePriority
      procedureTypeId
      procedureType
      procedureMod
      notes
      preOpNote
      anesthesiologistNotes
      orNotes
      anesthesiaType
      allergies
      patientName
      patientNameUnformatted
      patientAge
      patientSex
      patientId
      physicianId
      anesthesiologistId
      procedureHstId
      patientHstId
      physician {
        id
        name
        color
        notificationNumbers
        shouldSendNotification
        currentRoomId
        room {
          id
          name
        }
      }
      anesthesiologist {
        id
        name
        phoneNumber
        email
      }
      patient {
        id
        name
        nameUnformatted
        initials
        type
        status
        notificationNumbers
        caretakerConsent
        caretakerName
        caretakerNote
        medsDeliveryStatus
        bed
        room {
          id
          name
          type
        }
        events {
          admittedAt
          preOpAt
          readyForAnesthesiaAt
          spinalTimeoutAt
          readyForOrAt
          readyForSurgeonAt
          orAt
          orOutAt
          postOpAt
          recoveryAt
          dischargedAt
          holdProcedureAt
          blockNerveAt
          helpAt
          flipRoomAt
          helpActiveAt
          flipRoomActiveAt
          blockNerveActiveAt
        }
        caretakerThread {
          id
          hasUnreadMessages
        }
      }
      staffShifts {
        ...StaffShiftDataOnly
      }
      assignedVendors {
        id
        vendor {
          id
          name
        }
        shiftState
        implant
        price
        representative {
          name
          email
          phoneNumber
        }
      }
      vendorRepresentative {
        id
        vendor {
          name
        }
        implant
        price
        representative {
          name
          email
          phoneNumber
        }
      }
    }
  `,
  // Optimized fragment for schedule display - removes unused fields
  scheduleDisplay: gql`
    fragment ProcedureScheduleDisplayFragment on Procedure {
      id
      visitNumber
      date
      startTime
      startTimeText
      endTimeText
      duration
      orId
      or
      room
      status
      isCanceled
      color
      procedurePriority
      procedureType
      procedureMod
      notes
      preOpNote
      anesthesiologistNotes
      orNotes
      anesthesiaType
      allergies
      patientName
      patientInitials
      patientAge
      patientSex
      patientId
      physicianId
      anesthesiologistId
      procedureHstId
      patientHstId
      physician {
        id
        name
        color
        notificationNumbers
        shouldSendNotification
        currentRoomId
        room {
          id
          name
        }
      }
      anesthesiologist {
        id
        name
        phoneNumber
        email
      }
      patient {
        id
        name
        nameUnformatted
        initials
        type
        status
        notificationNumbers
        caretakerConsent
        caretakerName
        caretakerNote
        medsDeliveryStatus
        bed
        room {
          id
          name
          type
        }
        events {
          admittedAt
          preOpAt
          readyForAnesthesiaAt
          spinalTimeoutAt
          readyForOrAt
          readyForSurgeonAt
          orAt
          orOutAt
          postOpAt
          recoveryAt
          dischargedAt
          holdProcedureAt
          blockNerveAt
          helpAt
          flipRoomAt
          helpActiveAt
          flipRoomActiveAt
          blockNerveActiveAt
        }
        caretakerThread {
          id
          hasUnreadMessages
        }
      }
      staffShifts {
        ...StaffShiftDataSchedule
      }
      assignedVendors {
        id
        vendor {
          id
          name
        }
        shiftState
        implant
        price
        representative {
          name
          email
          phoneNumber
        }
      }
      vendorRepresentative {
        id
        vendor {
          name
        }
        implant
        price
        representative {
          name
          email
          phoneNumber
        }
      }
    }
  `,
  scheduleDisplayOptimized: gql`
    fragment ProcedureScheduleDisplayOptimized on Procedure {
      id
      visitNumber
      date
      startTime
      startTimeText
      endTimeText
      duration
      orId
      or
      room
      status
      isCanceled
      color
      procedurePriority
      procedureType
      procedureMod
      notes
      preOpNote
      anesthesiologistNotes
      orNotes
      anesthesiaType
      allergies
      patientName
      patientNameUnformatted
      patientDateOfBirth
      patientInitials
      patientAge
      patientSex
      patientId
      physicianId
      anesthesiologistId
      physician {
        id
        name
        color
        currentRoomId
      }
      anesthesiologist {
        id
        name
      }
      patient {
        id
        status
        bed
        events {
          admittedAt
          preOpAt
          readyForAnesthesiaAt
          spinalTimeoutAt
          readyForOrAt
          readyForSurgeonAt
          orAt
          orOutAt
          recoveryAt
          recoveryOutAt
          postOpAt
          postOpOutAt
          dischargedAt
          anestheticStartAt
          physicianInAt
          timeOutAt
          procedureStartAt
          procedureEndAt
          anestheticEndAt
          blockNerveActiveAt
          helpActiveAt
          flipRoomActiveAt
          holdProcedureAt
          dpuAt
        }
        room {
          id
          name
          type
        }
      }
      staffShifts {
        ...StaffShiftDataSchedule
      }
      assignedVendors {
        id
        vendor {
          id
          name
        }
        shiftState
      }
    }
  `,
  // Phase 2: Edit fields fragment (lazy loaded)
  editFields: gql`
    fragment ProcedureEditFields on Procedure {
      id
      visitNumber
      procedureHstId
      patientHstId
      physicianHstId
      anesthesiologistHstId
      procedureTypeId
      diagnosis
      equipment
      cptCodes
      bilaterally
      visitType
      insuranceName
      policyName
      preOpNote
      anesthesiologistNotes
      orNotes
      visit
      procedurePriority
      color
      patientDateOfBirthISO
      patientHomePhone
      patientCellPhone
      caretakerCellPhone
      familyCellPhone
      patientEmailAddress
      patientAddress
      physician {
        id
        name
        notificationNumbers
        email
      }
      anesthesiologist {
        id
        name
        phoneNumber
        email
      }
      vendorRepresentative {
        id
        vendor {
          name
        }
        implant
        price
        representative {
          name
          email
          phoneNumber
        }
      }
    }
  `,
  // Phase 2: PDF fields fragment (lazy loaded)
  pdfFields: gql`
    fragment ProcedurePDFFields on Procedure {
      id
      patientDateOfBirth
      patientHomePhone
      patientCellPhone
      caretakerCellPhone
      familyCellPhone
      patientEmailAddress
      patientAddress
      physician {
        id
        email
        notificationNumbers
      }
      anesthesiologist {
        id
        phoneNumber
        email
      }
    }
  `,
  base: gql`
    fragment ProcedureBase on Procedure {
     ${baseFields}
    }
  `,
  // Lightweight fragment for /scheduled-patients list view - only fields used by columns
  scheduledList: gql`
    fragment ProcedureScheduledList on Procedure {
      id
      patientId
      patientHstId
      patientName
      patientDateOfBirth
      patientAge
      patientCellPhone
      patientEmailAddress
      startTime
      duration
      procedureType
      physicianNameHST
      preferredLanguage
      status
      visit
    }
  `,
  questionnaire: gql`
    fragment Questionnaire on QuestionnaireAnswers {
      id
      questionnaireVersion
      progress
      questions
      answers
      progress
      submittedAt
      firstSubmittedAt
      interpreter
      heightFt
      heightIn
      weightLb
    }
  `,
  fromTo: gql`
    fragment ProcedureFromToData on Procedure {
      ${baseFields}
      physician {
        id
        name
        color
        notificationNumbers
        email
        currentRoomId
      }
      anesthesiologist {
        id
        name
        phoneNumber
        email
      }
    }
  `,
};

export const scheduleUserProcedures = gql`
  subscription scheduleUserProcedures($scheduleUserId: Long!, $scheduleUserType: String!, $offset: Int!, $limit: Int!) {
    scheduleUserProcedures(
      scheduleUserId: $scheduleUserId
      scheduleUserType: $scheduleUserType
      offset: $offset
      limit: $limit
    ) {
      totalCount
      entities {
        ...ProcedureData
        staffShifts {
          ...StaffShiftDataOnly
        }
      }
      hasNextPage
    }
  }
  ${fragments.all}
  ${staffFragments.shiftOnly}
`;

export const dailyProcedures = gql`
  query dailyProcedures($date: Date!) {
    procedures: dailyProcedures(date: $date) {
      ...ProcedureData
    }
  }
  ${fragments.all}
`;

export const patientProcedures = gql`
  query patientProcedures($patientId: Long!) {
    procedures: patientProcedures(patientId: $patientId) {
      ...ProcedureData
    }
  }
  ${fragments.all}
`;

const ChartingData = `
  id
  questionnaire {
    id
    name
    icon
    type
    content
    order
  }
  questionnaireExchange {
    id
    procedureId
    answers
    submittedAt
  }
  completed
  defaultAnswers
`;

const ChartingCompleted = `
  id
  completed
`;

const AllChartingData = `
  preOpCharts {
    ${ChartingData}
  }
  orCharts {
    ${ChartingData}
  }
  pacuCharts {
    ${ChartingData}
  }
  physicianCharts {
    ${ChartingData}
  }
  anesthesiologistPreOpCharts {
    ${ChartingData}
  }
  anesthesiologistOrCharts {
    ${ChartingData}
  }
  anesthesiologistPacuCharts {
    ${ChartingData}
  }
`;

export const scheduledProcedure = gql`
  subscription procedure($id: Long!) {
    scheduledProcedure: procedure(id: $id) {
      ...ProcedureBase

      anesthesiologist {
        id
        name
      }

      physician {
        id
        name
      }

      visits {
        id
        startTime
        procedureType
        visit
        isCanceled
      }

      events {
        id
        type
        timestamp
        patientPin
        channel
        content
        user {
          id
          name
        }
        text
        status
        anesthesiaReviewed
        exchangeId
      }

      patient {
        id
        notificationNumbers
        initials
        type
      }

      entryQuestionnaire {
        ...Questionnaire
      }
      ${AllChartingData}

      procedureInstructions
      entryQuestionnaireProgress
      questionnaireInviteSentAt
      instructionsInviteSentAt
      questionnaireInviteSMSCount
      questionnaireInviteEmailCount
      instructionLastSent {
        id
        name
        icon
        type
        content
        order
      }
      instructionsAccepted
      anesthesiaReviewed
      preOpDisplayData {
        progress
        interpreter
        heightFt
        heightIn
        weightLb
      }
    }
  }
  ${fragments.base}
  ${fragments.questionnaire}
`;

export const scheduledProcedureWithPhysician = (physicianIds: number[]) => gql`
  subscription procedureWithPhysician($id: Long!) {
    scheduledProcedure: procedureWithPhysician(id: $id, physicians: ${JSON.stringify(physicianIds)}) {
      ...ProcedureBase

      anesthesiologist {
        id
        name
      }

      physician {
        id
        name
      }

      visits {
        id
        startTime
        procedureType
        visit
        isCanceled
      }

      events {
        id
        type
        timestamp
        patientPin
        channel
        content
        user {
          id
          name
        }
        text
        status
        anesthesiaReviewed
        exchangeId
      }

      patient {
        id
        notificationNumbers
        initials
        type
      }

      entryQuestionnaire {
        ...Questionnaire
      }
      ${AllChartingData}

      procedureInstructions
      entryQuestionnaireProgress
      questionnaireInviteSentAt
      instructionsInviteSentAt
      questionnaireInviteSMSCount
      questionnaireInviteEmailCount
      instructionsAccepted
      anesthesiaReviewed
      preOpDisplayData {
        progress
        interpreter
        heightFt
        heightIn
        weightLb
      }
    }
  }
  ${fragments.base}
  ${fragments.questionnaire}
`;

export const inTakeForm = gql`
  subscription procedure($id: Long!) {
    registrationPackage: procedure(id: $id) {
      ...ProcedureBase
      intakeStatus

      anesthesiologist {
        id
        name
      }

      physician {
        id
        name
      }

      visits {
        id
        startTime
        procedureType
        visit
        isCanceled
      }

      events {
        id
        type
        timestamp
        patientPin
        channel
        content
        user {
          id
          name
        }
        text
        status
        anesthesiaReviewed
        exchangeId
      }

      patient {
        id
        notificationNumbers
        initials
        type
      }

      inTakeForm {
        ...Questionnaire
      }

      entryQuestionnaire {
        heightFt
        heightIn
        weightLb
      }
      procedureInstructions
      inTakeFormProgress
      inTakeFormInviteSentAt
      instructionsInviteSentAt
      instructionsAccepted
      anesthesiaReviewed
    }
  }
  ${fragments.base}
  ${fragments.questionnaire}
`;

export const listSubscription = gql`
  subscription ($date: Date!, $physician: Long, $operationRoomId: Long, $isCanceled: Boolean) {
    procedures(date: $date, physician: $physician, operationRoomId: $operationRoomId, isCanceled: $isCanceled) {
      ...ProcedureData
    }
  }
  ${fragments.all}
`;

export const getProceduresFromToSubscription = gql`
  query ($from: Date!, $to: Date!, $physician: Long, $operationRoomId: Long, $isCanceled: Boolean) {
    proceduresFromTo(
      from: $from
      to: $to
      physician: $physician
      operationRoomId: $operationRoomId
      isCanceled: $isCanceled
    ) {
      ...ProcedureFromToData
    }
  }
  ${fragments.fromTo}
`;

export const listProceduresFromToSubscription = gql`
  subscription ($from: Date!, $to: Date!, $physician: Long, $operationRoomId: Long, $isCanceled: Boolean) {
    proceduresFromTo(
      from: $from
      to: $to
      physician: $physician
      operationRoomId: $operationRoomId
      isCanceled: $isCanceled
    ) {
      ...ProcedureFromToData
    }
  }
  ${fragments.fromTo}
`;

export const listWithStaffShiftSubscription = gql`
  subscription ($date: Date!, $physician: Long, $operationRoomId: Long, $isCanceled: Boolean) {
    procedures(date: $date, physician: $physician, operationRoomId: $operationRoomId, isCanceled: $isCanceled) {
      ...ProcedureScheduleFragment
    }
  }
  ${fragments.schedule}
  ${staffFragments.shiftOnly}
`;

export const scheduleProceduresSubscription = gql`
  subscription scheduleProcedures($date: Date!, $physician: Long, $operationRoomId: Long, $isCanceled: Boolean) {
    procedures(date: $date, physician: $physician, operationRoomId: $operationRoomId, isCanceled: $isCanceled) {
      ...ProcedureScheduleDisplayFragment
    }
  }
  ${fragments.scheduleDisplay}
  ${staffFragments.shiftSchedule}
`;

// Phase 2 & 3: Optimized subscription with normalization (60-75% payload reduction)
export const scheduleProceduresOptimizedSubscription = gql`
  subscription ScheduleUpdatedIncremental($date: Date!, $physician: Long) {
    scheduleUpdatedIncremental(date: $date, physician: $physician) {
      ...ProcedureScheduleDisplayOptimized
    }
  }
  ${fragments.scheduleDisplayOptimized}
  ${staffFragments.shiftSchedule}
`;

// Phase 4: Query version - kept for backward compatibility
export const scheduleProceduresOptimizedQuery = gql`
  query scheduleProcedures($date: Date!, $physician: Long, $operationRoomId: Long, $isCanceled: Boolean) {
    procedures(date: $date, physician: $physician, operationRoomId: $operationRoomId, isCanceled: $isCanceled) {
      ...ProcedureScheduleDisplayOptimized
    }
  }
  ${fragments.scheduleDisplayOptimized}
  ${staffFragments.shiftSchedule}
`;

// Phase 2: Lazy loading queries
export const procedureEditFieldsQuery = gql`
  query procedureEditFields($id: Long!) {
    procedureEditFields(id: $id) {
      ...ProcedureEditFields
    }
  }
  ${fragments.editFields}
`;

export const procedurePDFFieldsQuery = gql`
  query procedurePDFFields($ids: [Long!]!) {
    procedurePDFFields(ids: $ids) {
      ...ProcedurePDFFields
    }
  }
  ${fragments.pdfFields}
`;

// Phase 3: Normalization queries - fetch daily entities once
export const dailyScheduleEntitiesQuery = gql`
  query dailyScheduleEntities($date: Date!) {
    physicians: dailyPhysicians(date: $date) {
      id
      name
      color
      notificationNumbers
      shouldSendNotification
      currentRoomId
      room {
        id
        name
      }
    }
    anesthesiologists: dailyAnesthesiologists(date: $date) {
      id
      name
      phoneNumber
      email
    }
    patients: dailyPatients(date: $date) {
      id
      name
      nameUnformatted
      initials
      type
      status
      notificationNumbers
      caretakerConsent
      caretakerName
      caretakerNote
      medsDeliveryStatus
      bed
      room {
        id
        name
        type
      }
      events {
        admittedAt
        preOpAt
        readyForAnesthesiaAt
        spinalTimeoutAt
        readyForOrAt
        readyForSurgeonAt
        orAt
        orOutAt
        postOpAt
        recoveryAt
        dischargedAt
        holdProcedureAt
        blockNerveAt
        helpAt
        flipRoomAt
        helpActiveAt
        flipRoomActiveAt
        blockNerveActiveAt
        anestheticStartAt
        physicianInAt
        timeOutAt
        readyForSurgeryAt
        procedureStartAt
        calledNextPatientAt
        closingStartAt
        surgeonLeftAt
        procedureEndAt
        anestheticEndAt
      }
      caretakerThread {
        id
        hasUnreadMessages
      }
    }
  }
`;

export const proceduresDayOverSubscription = gql`
  subscription proceduresDayOver($date: Date!, $physician: Long) {
    procedures(date: $date, physician: $physician) {
      id
      isCanceled
      startTime
      duration
      color
      patient {
        events {
          recoveryAt
          dischargedAt
        }
      }
      staffShifts {
        id
        shiftState
        staff {
          id
          staffId
          name
          titleAbbr
          primarySpecialty
          phoneNumber
        }
        from
        to
        additionalText
        note
        hourlyRate
        openPosition {
          primarySpecialty
          otherSpecialties
          title
          description
        }
      }
      assignedVendors {
        id
        vendor {
          id
          name
        }
      }
    }
  }
`;

export const detail = gql`
  query procedureDetail($id: Long!, $date: Date) {
    procedureDetail: procedure(id: $id) {
      ...ProcedureData
      staffShifts {
        ...StaffShiftDataFull
      }
    }
  }
  ${fragments.all}
  ${staffFragments.shift}
`;

export const hasScheduleAccessAllRight = gql`
  query hasScheduleAccessAllRight($scheduleUserId: Long!, $scheduleUserType: String!) {
    hasScheduleAccessAllRight(scheduleUserId: $scheduleUserId, scheduleUserType: $scheduleUserType)
  }
`;

export const unregisteredPatients = gql`
  subscription ($date: Date!) {
    patients: unregisteredProcedures(date: $date) {
      ...ProcedureBase
      readyToRegisterNotificationEnabled
      physician {
        name
      }
      events {
        id
        type
        timestamp
        channel
        content
        text
        status
      }
    }
  }
  ${fragments.base}
`;

export const arrivedUnregisteredPatients = gql`
  subscription ($date: Date!) {
    arrivals: arrivedUnregisteredProcedures(date: $date) {
      ...ProcedureBase
      readyToRegisterNotificationEnabled
      physician {
        name
      }
      events {
        id
        type
        timestamp
        channel
        content
        text
        status
      }
      arrivedAt
    }
  }
  ${fragments.base}
`;

export const setProcedureShiftState = gql`
  mutation setProcedureShiftState(
    $procedureId: Long!
    $scheduleUserType: String!
    $scheduleUserId: Long!
    $state: ShiftState!
  ) {
    setProcedureShiftState(
      procedureId: $procedureId
      scheduleUserType: $scheduleUserType
      scheduleUserId: $scheduleUserId
      state: $state
    )
  }
`;

export const scheduleProcedure = gql`
  mutation scheduleProcedure(
    $operationRoomId: Long!
    $date: String!
    $startTime: String!
    $duration: Int!
    $physicianId: Long!
    $anesthesiologistId: Long
    $anesthesiaType: String
    $patientProviderId: HstId!
    $patientName: String!
    $patientDOB: String
    $patientSex: String
    $procedureTypeId: Long!
    $procedureType: String!
    $notes: String
    $allergies: String
    $patientHomePhone: String
    $patientCellPhone: String
    $vendorId: Long
    $implant: String
    $price: String
    $representative: RepresentativeInput
    $diagnosis: String
    $equipment: String
    $cptCodes: String
    $bilaterally: String
    $visitType: String
    $room: String
    $insuranceName: String
    $policyName: String
    $caretakerCellPhone: String
    $familyCellPhone: String
    $patientEmailAddress: String
    $patientAddress: String
    $procedurePriority: Int
    $anesthesiologistNotes: String
    $orNotes: String
    $preOpNote: String
  ) {
    scheduleProcedure(
      operationRoomId: $operationRoomId
      date: $date
      startTime: $startTime
      duration: $duration
      physicianId: $physicianId
      anesthesiologistId: $anesthesiologistId
      anesthesiaType: $anesthesiaType
      patientProviderId: $patientProviderId
      patientName: $patientName
      patientDOB: $patientDOB
      patientSex: $patientSex
      procedureTypeId: $procedureTypeId
      procedureType: $procedureType
      notes: $notes
      allergies: $allergies
      patientHomePhone: $patientHomePhone
      patientCellPhone: $patientCellPhone
      vendorId: $vendorId
      implant: $implant
      price: $price
      representative: $representative
      diagnosis: $diagnosis
      equipment: $equipment
      cptCodes: $cptCodes
      bilaterally: $bilaterally
      visitType: $visitType
      room: $room
      insuranceName: $insuranceName
      policyName: $policyName
      caretakerCellPhone: $caretakerCellPhone
      familyCellPhone: $familyCellPhone
      patientEmailAddress: $patientEmailAddress
      patientAddress: $patientAddress
      procedurePriority: $procedurePriority
      anesthesiologistNotes: $anesthesiologistNotes
      orNotes: $orNotes
      preOpNote: $preOpNote
    ) {
      ...ProcedureData
    }
  }
  ${fragments.all}
`;

export const editScheduledProcedure = gql`
  mutation editScheduledProcedure(
    $procedureId: Long!
    $operationRoomId: Long!
    $date: String!
    $startTime: String!
    $duration: Int!
    $physicianId: Long!
    $anesthesiologistId: Long
    $anesthesiaType: String
    $patientProviderId: HstId!
    $patientName: String!
    $patientDOB: String
    $patientSex: String
    $procedureTypeId: Long!
    $procedureType: String!
    $notes: String
    $allergies: String
    $patientHomePhone: String
    $patientCellPhone: String
    $vendorId: Long
    $implant: String
    $price: String
    $representative: RepresentativeInput
    $diagnosis: String
    $equipment: String
    $cptCodes: String
    $bilaterally: String
    $visitType: String
    $room: String
    $insuranceName: String
    $policyName: String
    $caretakerCellPhone: String
    $familyCellPhone: String
    $patientEmailAddress: String
    $patientAddress: String
    $procedurePriority: Int
    $anesthesiologistNotes: String
    $orNotes: String
    $preOpNote: String
  ) {
    editScheduledProcedure(
      procedureId: $procedureId
      operationRoomId: $operationRoomId
      date: $date
      startTime: $startTime
      duration: $duration
      physicianId: $physicianId
      anesthesiologistId: $anesthesiologistId
      anesthesiaType: $anesthesiaType
      patientProviderId: $patientProviderId
      patientName: $patientName
      patientDOB: $patientDOB
      patientSex: $patientSex
      procedureTypeId: $procedureTypeId
      procedureType: $procedureType
      notes: $notes
      allergies: $allergies
      patientHomePhone: $patientHomePhone
      patientCellPhone: $patientCellPhone
      vendorId: $vendorId
      implant: $implant
      price: $price
      representative: $representative
      diagnosis: $diagnosis
      equipment: $equipment
      cptCodes: $cptCodes
      bilaterally: $bilaterally
      visitType: $visitType
      room: $room
      insuranceName: $insuranceName
      policyName: $policyName
      caretakerCellPhone: $caretakerCellPhone
      familyCellPhone: $familyCellPhone
      patientEmailAddress: $patientEmailAddress
      patientAddress: $patientAddress
      procedurePriority: $procedurePriority
      anesthesiologistNotes: $anesthesiologistNotes
      orNotes: $orNotes
      preOpNote: $preOpNote
    )
  }
`;

export const editProcedure = gql`
  mutation (
    $id: Long!
    $operationRoom: String!
    $date: String!
    $startTime: String!
    $duration: Int!
    $physicianId: Long!
    $anesthesiologistId: Long
    $anesthesiaType: String
    $patientName: String!
    $patientAge: Age
    $patientSex: String
    $procedureType: String!
    $notes: String
    $allergies: String
    $patientHomePhone: String
    $patientCellPhone: String
    $procedurePriority: Int
    $color: String
    $anesthesiologistNotes: String
    $orNotes: String
    $preOpNote: String
  ) {
    editProcedure(
      id: $id
      operationRoom: $operationRoom
      date: $date
      startTime: $startTime
      duration: $duration
      physicianId: $physicianId
      anesthesiologistId: $anesthesiologistId
      anesthesiaType: $anesthesiaType
      patientName: $patientName
      patientAge: $patientAge
      patientSex: $patientSex
      procedureType: $procedureType
      notes: $notes
      allergies: $allergies
      patientHomePhone: $patientHomePhone
      patientCellPhone: $patientCellPhone
      procedurePriority: $procedurePriority
      color: $color
      anesthesiologistNotes: $anesthesiologistNotes
      orNotes: $orNotes
      preOpNote: $preOpNote
    ) {
      ...ProcedureData
    }
  }
  ${fragments.all}
`;

export const editProcedureAnesthesiologist = gql`
  mutation ($id: Long!, $anesthesiologistId: Long) {
    editProcedureAnesthesiologist(id: $id, anesthesiologistId: $anesthesiologistId) {
      ...ProcedureData
    }
  }
  ${fragments.all}
`;

export const editProcedurePriority = gql`
  mutation ($id: Long!, $procedurePriority: Int) {
    editProcedurePriority(id: $id, procedurePriority: $procedurePriority) {
      ...ProcedureData
    }
  }
  ${fragments.all}
`;

export const editColor = gql`
  mutation ($id: Long!, $color: String) {
    editColor(id: $id, color: $color) {
      ...ProcedureData
    }
  }
  ${fragments.all}
`;

export const editColors = gql`
  mutation editColors($procedureIds: [Long!]!, $color: String) {
    editColors(procedureIds: $procedureIds, color: $color)
  }
`;

export const editAnesthesiologistNotes = gql`
  mutation ($id: Long!, $anesthesiologistNotes: String) {
    editAnesthesiologistNotes(id: $id, anesthesiologistNotes: $anesthesiologistNotes) {
      ...ProcedureData
    }
  }
  ${fragments.all}
`;

export const editOrNotes = gql`
  mutation ($id: Long!, $orNotes: String) {
    editOrNotes(id: $id, orNotes: $orNotes) {
      ...ProcedureData
    }
  }
  ${fragments.all}
`;

export const editProcedurePreOpNote = gql`
  mutation ($id: Long!, $preOpNote: String) {
    editProcedurePreOpNote(id: $id, preOpNote: $preOpNote) {
      ...ProcedureData
    }
  }
  ${fragments.all}
`;

export const editProcedureAnesthesiaType = gql`
  mutation ($id: Long!, $anesthesiaType: String!) {
    editProcedureAnesthesiaType(id: $id, anesthesiaType: $anesthesiaType) {
      ...ProcedureData
    }
  }
  ${fragments.all}
`;

const update = gql`
  mutation (
    $id: Long!
    $patientName: String
    $patientDateOfBirth: Date
    $notes: String
    $allergies: String
    $patientHomePhone: String
    $patientCellPhone: String
    $patientEmailAddress: String
  ) {
    editProcedureEntry(
      id: $id
      patientName: $patientName
      patientDateOfBirth: $patientDateOfBirth
      notes: $notes
      allergies: $allergies
      patientHomePhone: $patientHomePhone
      patientCellPhone: $patientCellPhone
      patientEmailAddress: $patientEmailAddress
    ) {
      ...ProcedureData
    }
  }
  ${fragments.all}
`;

const updateMobileNumber = gql`
  mutation ($id: Long!, $patientCellPhone: String) {
    editProcedureEntry(id: $id, patientCellPhone: $patientCellPhone) {
      ...ProcedureData
    }
  }
  ${fragments.all}
`;

export const cancelProcedure = gql`
  mutation ($id: Long!, $reason: String) {
    cancelProcedure(id: $id, reason: $reason)
  }
`;

export const reactivateProcedure = gql`
  mutation ($id: Long!) {
    reactivateProcedure(id: $id)
  }
`;

export const item = gql`
  query procedure($id: Long!) {
    scheduledProcedure: procedure(id: $id) {
      ...ProcedureBase
      events {
        id
        type
        timestamp
        patientPin
        channel
        content
        user {
          id
          name
        }
        text
        status
        exchangeId
      }

      patient {
        id
        notificationNumbers
      }

      entryQuestionnaire {
        ...Questionnaire
      }

      procedureInstructions
      instructionsAccepted
      anesthesiaReviewed
    }
  }
  ${fragments.base}
  ${fragments.questionnaire}
`;

export const list = gql`
  query procedures($filter: Filter) {
    scheduledProcedures: procedures(filter: $filter) {
      ...ProcedureBase
      entryQuestionnaire {
        id
        progress
        submittedAt
        firstSubmittedAt
      }
      events {
        id
        type
        timestamp
        patientPin
        channel
        text
        status
        content
        exchangeId
      }
      instructionsAccepted
    }
  }
  ${fragments.base}
`;

export const scheduledProceduresSubscription = gql`
  subscription scheduledProcedures($filter: Filter) {
    scheduledProcedures(filter: $filter) {
      ...ProcedureScheduledList
      questionnaireInviteSentAt
      instructionsInviteSentAt
      instructionsAccepted
      questionnaireInviteSMSCount
      questionnaireInviteEmailCount
      preOpDisplayData {
        progress
        interpreter
        heightFt
        heightIn
        weightLb
      }
      physician {
        id
        name
      }
      lastQuestionnaireInviteSMS {
        id
        type
        timestamp
        patientPin
        channel
        text
        status
        content
        exchangeId
      }
      events {
        id
        type
        timestamp
        patientPin
        channel
        content
        user {
          id
          name
        }
        text
        status
        anesthesiaReviewed
        exchangeId
      }
      anesthesiaReviewed
      docsLastPrintedAt
      filesUploaded
    }
  }
  ${fragments.scheduledList}
`;

export const scheduledProceduresWithPhysicianSubscription = (physicianIds: number[]) => gql`
  subscription scheduledProceduresWithPhysician($filter: Filter) {
    scheduledProcedures: scheduledProceduresWithPhysician(filter: $filter, physicians: ${JSON.stringify(
      physicianIds
    )}) {
      ...ProcedureScheduledList
      questionnaireInviteSentAt
      instructionsInviteSentAt
      instructionsAccepted
      questionnaireInviteSMSCount
      questionnaireInviteEmailCount
      preOpDisplayData {
        progress
        interpreter
        heightFt
        heightIn
        weightLb
      }
      physician {
        id
        name
      }
      lastQuestionnaireInviteSMS {
        id
        type
        timestamp
        patientPin
        channel
        text
        status
        content
        exchangeId
      }
      events {
        id
        type
        timestamp
        patientPin
        channel
        content
        user {
          id
          name
        }
        text
        status
        anesthesiaReviewed
        exchangeId
      }
      anesthesiaReviewed
      docsLastPrintedAt
      filesUploaded
    }
  }
  ${fragments.scheduledList}
`;

export const inTakeFormsSubscription = gql`
  subscription registrationPackages($filter: Filter) {
    registrationPackages(filter: $filter) {
      ...ProcedureScheduledList
      intakeStatus
      inTakeFormProgress
      inTakeFormInviteSentAt
      instructionsInviteSentAt
      instructionsAccepted
      inTakeFormInviteSMSCount
      inTakeFormInviteEmailCount
      preOpDisplayData {
        progress
        interpreter
        heightFt
        heightIn
        weightLb
      }
      physician {
        id
        name
      }
      lastQuestionnaireInviteSMS {
        id
        type
        timestamp
        patientPin
        channel
        text
        status
        content
        exchangeId
      }
      events {
        id
        type
        timestamp
        patientPin
        channel
        content
        user {
          id
          name
        }
        text
        status
        anesthesiaReviewed
        exchangeId
      }
      anesthesiaReviewed
      docsLastPrintedAt
      filesUploaded
    }
  }
  ${fragments.scheduledList}
`;

export const procedureStatistics = gql`
  subscription procedureStatistics($filter: Filter) {
    procedureStatistics(filter: $filter) {
      scheduledProcedures
      formsFilled
      formsNotFilled
      totalForms
      pendingInfo
      preOpCompleted
      instructionsAccepted
    }
  }
`;

export const procedureStatisticsWithPhysician = (physicianIds: number[]) => gql`
  subscription procedureStatisticsWithPhysician($filter: Filter) {
    procedureStatistics: procedureStatisticsWithPhysician(filter: $filter, physicians: ${JSON.stringify(
      physicianIds
    )}) {
      scheduledProcedures
      formsFilled
      formsNotFilled
      totalForms
      pendingInfo
      preOpCompleted
      instructionsAccepted
    }
  }
`;
export const intakeStatistics = gql`
  subscription intakeStatistics($filter: Filter) {
    intakeStatistics(filter: $filter) {
      scheduledProcedures
      totalForms
    }
  }
`;

export const scheduledProceduresViews = gql`
  subscription scheduledProceduresViews($filter: Filter) {
    scheduledProceduresViews(filter: $filter) {
      questionnairesNotSent
      incompleteQuestionnaires
    }
  }
`;

export const resendQuestionnaireInvitationSMS = gql`
  mutation resendQuestionnaireInvitationSMS($procedureId: Long!) {
    resendQuestionnaireInvitationSMS(procedureId: $procedureId)
  }
`;

export const resendInTakeFormInvitationSMS = gql`
  mutation resendInTakeFormInvitationSMS($procedureId: Long!) {
    resendInTakeFormInvitationSMS(procedureId: $procedureId)
  }
`;

export const resendRegisterReadySMS = gql`
  mutation resendRegisterReadySMS($procedureId: Long!) {
    resendRegisterReadySMS(procedureId: $procedureId)
  }
`;

export const resendQuestionnaireInvitationEmail = gql`
  mutation resendQuestionnaireInvitationEmail($procedureId: Long!) {
    resendQuestionnaireInvitationEmail(procedureId: $procedureId)
  }
`;

export const resendInTakeFormInvitationEmail = gql`
  mutation resendInTakeFormInvitationEmail($procedureId: Long!) {
    resendInTakeFormInvitationEmail(procedureId: $procedureId)
  }
`;

export const resendInstructionInvitationSMS = gql`
  mutation resendInstructionInvitationSMS($procedureId: Long!, $templateType: InstructionTemplateType!) {
    resendInstructionInvitationSMS(procedureId: $procedureId, templateType: $templateType)
  }
`;

export const resendInstructionInvitationEmail = gql`
  mutation resendInstructionInvitationEmail($procedureId: Long!, $templateType: InstructionTemplateType!) {
    resendInstructionInvitationEmail(procedureId: $procedureId, templateType: $templateType)
  }
`;

export const setStatus = gql`
  mutation setProcedureStatus($id: Long!, $status: String) {
    setProcedureStatus(id: $id, status: $status) {
      id
      status
    }
  }
`;

export const setIntakeStatus = gql`
  mutation setIntakeStatus($id: Long!, $status: String) {
    setIntakeStatus(id: $id, status: $status) {
      id
      status
    }
  }
`;

export const setAnesthesiaReviewed = gql`
  mutation setAnesthesiaReviewed($id: Long!, $anesthesiaReviewed: Boolean!) {
    setAnesthesiaReviewed(id: $id, anesthesiaReviewed: $anesthesiaReviewed) {
      id
      anesthesiaReviewed
    }
  }
`;

export const postPreopNote = gql`
  mutation postPreopNote($id: Long!, $preopNote: String!) {
    postPreopNote(id: $id, preopNote: $preopNote) {
      id
      events {
        id
        type
        timestamp
        patientPin
        channel
        user {
          id
          name
        }
        text
        status
        exchangeId
      }
    }
  }
`;

export const deletePreopNote = gql`
  mutation deletePreOpNote($id: Long!) {
    deletePreOpNote(id: $id)
  }
`;

export const postAnesthesiaNote = gql`
  mutation postAnesthesiaNote($procedureId: Long!, $note: String!) {
    postAnesthesiaNote(procedureId: $procedureId, note: $note) {
      id
      events {
        id
        type
        timestamp
        patientPin
        channel
        user {
          id
          name
        }
        text
        status
        exchangeId
      }
    }
  }
`;

export const deleteAnesthesiaNote = gql`
  mutation deleteAnesthesiaNote($noteId: Long!) {
    deleteAnesthesiaNote(noteId: $noteId)
  }
`;

export const getSurgeryInstructionsTemplates = gql`
  query surgeryInstructionsTemplates {
    surgeryInstructionsTemplates {
      id
      type
      content
    }
  }
`;

export const updateSurgeryInstructionsTemplate = gql`
  mutation updateSurgeryInstructionsTemplate($templateType: InstructionTemplateType!, $content: String!) {
    updateSurgeryInstructionsTemplate(templateType: $templateType, content: $content)
  }
`;

export const updateProcedureSurgeryInstructions = gql`
  mutation updateProcedureSurgeryInstructions($procedureId: Long!, $content: String!) {
    updateProcedureSurgeryInstructions(procedureId: $procedureId, content: $content)
  }
`;

export const resetProcedureInstructions = gql`
  mutation resetProcedureInstructions($procedureId: Long!) {
    resetProcedureInstructions(procedureId: $procedureId)
  }
`;

export const searchProcedures = gql`
  query searchProcedures($filter: ProcedureFilter!) {
    searchProcedures(filter: $filter) {
      id
      patientHstId
      patientName
      patientDateOfBirthISO
      startTime
      visit
      physicianName
      procedureType
    }
  }
`;

export const getScheduleNotificationConfiguration = gql`
  query scheduleNotificationConfiguration {
    scheduleNotificationConfiguration {
      notificationRecipients
    }
  }
`;

export const updateScheduleNotificationConfig = gql`
  mutation updateScheduleNotificationConfig($notificationRecipients: [Long!]!) {
    updateScheduleNotificationConfig(notificationRecipients: $notificationRecipients)
  }
`;

// ============================================================================
// PAGINATED QUERIES - Server-side pagination for optimal performance
// ============================================================================

/**
 * Paginated scheduled procedures query.
 * Uses SQL LIMIT/OFFSET for efficient server-side pagination.
 * Recommended for list views with large datasets.
 */
export const scheduledProceduresPaginatedQuery = gql`
  query scheduledProceduresPaginated(
    $filter: Filter
    $offset: Int = 0
    $limit: Int = 50
    $sortBy: String
    $sortOrder: String
  ) {
    scheduledProceduresPaginated(
      filter: $filter
      offset: $offset
      limit: $limit
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      ...ProcedureScheduledList
      questionnaireInviteSentAt
      instructionsInviteSentAt
      instructionsAccepted
      questionnaireInviteSMSCount
      questionnaireInviteEmailCount
      lastQuestionnaireInviteSMS {
        id
        type
        timestamp
        patientPin
        channel
        text
        status
        content
        exchangeId
      }
      preOpDisplayData {
        progress
        interpreter
        heightFt
        heightIn
        weightLb
      }
      physician {
        id
        name
      }
      anesthesiaReviewed
      docsLastPrintedAt
      filesUploaded
    }
  }
  ${fragments.scheduledList}
`;

/**
 * Count query for scheduled procedures.
 * Returns total count matching the filter for pagination controls.
 */
export const scheduledProceduresCountQuery = gql`
  query scheduledProceduresCount($filter: Filter) {
    scheduledProceduresCount(filter: $filter)
  }
`;

/**
 * Paginated query for registration packages (intake forms).
 * Uses SQL LIMIT/OFFSET for efficient server-side pagination.
 */
export const registrationPackagesPaginatedQuery = gql`
  query registrationPackagesPaginated($filter: Filter, $offset: Int, $limit: Int, $sortBy: String, $sortOrder: String) {
    registrationPackagesPaginated(
      filter: $filter
      offset: $offset
      limit: $limit
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      ...ProcedureScheduledList
      intakeStatus
      inTakeFormProgress
      inTakeFormInviteSentAt
      instructionsInviteSentAt
      instructionsAccepted
      inTakeFormInviteSMSCount
      inTakeFormInviteEmailCount
      preOpDisplayData {
        progress
        interpreter
        heightFt
        heightIn
        weightLb
      }
      physician {
        id
        name
      }
      docsLastPrintedAt
      filesUploaded
    }
  }
  ${fragments.scheduledList}
`;

/**
 * Count query for registration packages.
 * Returns total count matching the filter for pagination controls.
 */
export const registrationPackagesCountQuery = gql`
  query registrationPackagesCount($filter: Filter) {
    registrationPackagesCount(filter: $filter)
  }
`;

// Note: procedureUpdatedSubscription was removed as the backend didn't have it.
// Use proceduresByIdSubscription for efficient IDs-based real-time updates.

/**
 * IDs-based subscription for efficient paginated real-time updates.
 * Only subscribes to specific procedure IDs (e.g., items on current page).
 *
 * @param procedureIds - Array of procedure IDs to subscribe to
 *
 * @example
 * // After loading paginated data, subscribe to current page items
 * const pageItemIds = data.scheduledProceduresPaginated.map(p => p.id);
 * useSubscription(proceduresByIdSubscription, { variables: { procedureIds: pageItemIds } });
 */
export const proceduresByIdSubscription = gql`
  subscription proceduresById($procedureIds: [Long!]!) {
    proceduresById(procedureIds: $procedureIds) {
      ...ProcedureScheduledList
      questionnaireInviteSMSCount
      questionnaireInviteEmailCount
      lastQuestionnaireInviteSMS {
        id
        type
        timestamp
        patientPin
        channel
        text
        status
        content
        exchangeId
      }
      preOpDisplayData {
        progress
        interpreter
      }
      anesthesiaReviewed
      docsLastPrintedAt
      filesUploaded
    }
  }
  ${fragments.scheduledList}
`;

export default {
  item,
  list,
  scheduledProceduresSubscription,
  inTakeFormsSubscription,
  update,
  scheduledProcedure,
  inTakeForm,
  detail,
  listSubscription,
  dailyProcedures,
  unregisteredPatients,
  updateMobileNumber,
  // Paginated queries for server-side pagination
  scheduledProceduresPaginatedQuery,
  scheduledProceduresCountQuery,
  registrationPackagesPaginatedQuery,
  registrationPackagesCountQuery,
  // IDs-based subscription for efficient real-time updates
  proceduresByIdSubscription,
};
